import { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);       // null = checking, false = not auth'd
  const [loading, setLoading] = useState(true);
  const [usage, setUsage] = useState(null);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(data);
    } catch (err) {
      if (process.env.NODE_ENV === "development") console.error("Auth check failed:", err.message);
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchUsage = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/usage`, { withCredentials: true });
      setUsage(data.usage);
    } catch (err) {
      if (process.env.NODE_ENV === "development") console.error("Usage fetch failed:", err.message);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  useEffect(() => {
    if (user) fetchUsage();
  }, [user, fetchUsage]);

  const login = useCallback(async (email, password) => {
    try {
      const { data } = await axios.post(
        `${API}/auth/login`,
        { email, password },
        { withCredentials: true }
      );
      setUser(data);
      return { success: true };
    } catch (e) {
      return {
        success: false,
        error: formatApiErrorDetail(e.response?.data?.detail) || e.message,
      };
    }
  }, []);

  const register = useCallback(async (name, email, password) => {
    try {
      const { data } = await axios.post(
        `${API}/auth/register`,
        { name, email, password },
        { withCredentials: true }
      );
      setUser(data);
      return { success: true };
    } catch (e) {
      return {
        success: false,
        error: formatApiErrorDetail(e.response?.data?.detail) || e.message,
      };
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (err) {
      if (process.env.NODE_ENV === "development") console.error("Logout error:", err.message);
    }
    setUser(false);
    setUsage(null);
  }, []);

  const refreshToken = useCallback(async () => {
    try {
      await axios.post(`${API}/auth/refresh`, {}, { withCredentials: true });
      await checkAuth();
    } catch (err) {
      if (process.env.NODE_ENV === "development") console.error("Token refresh failed:", err.message);
      setUser(false);
    }
  }, [checkAuth]);

  const contextValue = useMemo(
    () => ({ user, loading, usage, login, register, logout, refreshToken, fetchUsage }),
    [user, loading, usage, login, register, logout, refreshToken, fetchUsage]
  );

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
